import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/* ─── Helpers ──────────────────────────────────────────────────────── */
function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}
function fmtDateShort(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
}
function fmtDuration(min) {
  if (!min) return '';
  const h = Math.floor(min / 60), m = min % 60;
  return m ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
}
function addMinutes(time, min) {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + (min || 0);
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}
function capitalize(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

const MORNING   = ['08:00','09:00','10:00','11:00','12:00'];
const AFTERNOON = ['14:00','15:00','16:00','17:00','18:00'];

/* ─── CSS ──────────────────────────────────────────────────────────── */
const STYLES = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .rc {
    --ink:        #1C1814;
    --ink2:       #6B6560;
    --ink3:       #A8A3A0;
    --bg:         #F7F3EE;
    --surface:    #FFFFFF;
    --border:     #EBE5DC;
    --accent:     #1A8A7A;
    --accent-dk:  #0F5F54;
    --accent-lt:  #E8F4F2;
    --gold:       #C9922A;
    --gold-lt:    #FDF4E3;
    --green:      #22863A;
    --green-lt:   #E9F7EE;
    --r:          20px;
    --r-sm:       14px;
    font-family: system-ui,-apple-system,'Segoe UI',Helvetica,sans-serif;
    background: var(--bg);
    min-height: 100svh;
    color: var(--ink);
  }
  @media (prefers-color-scheme: dark) {
    .rc {
      --ink:#EDE8E2;--ink2:#8C8780;--ink3:#5A5550;
      --bg:#0E100F;--surface:#1A1F1E;--border:#2A302E;
      --accent:#22B5A4;--accent-dk:#40D4C4;--accent-lt:#0F2A28;
      --gold:#E0B96A;--gold-lt:#1C1A10;
      --green:#34D058;--green-lt:#0A1F10;
    }
  }
  :root[data-theme="dark"]  .rc { --ink:#EDE8E2;--ink2:#8C8780;--ink3:#5A5550;--bg:#0E100F;--surface:#1A1F1E;--border:#2A302E;--accent:#22B5A4;--accent-dk:#40D4C4;--accent-lt:#0F2A28;--gold:#E0B96A;--gold-lt:#1C1A10;--green:#34D058;--green-lt:#0A1F10; }
  :root[data-theme="light"] .rc { --ink:#1C1814;--ink2:#6B6560;--ink3:#A8A3A0;--bg:#F7F3EE;--surface:#FFFFFF;--border:#EBE5DC;--accent:#1A8A7A;--accent-dk:#0F5F54;--accent-lt:#E8F4F2;--gold:#C9922A;--gold-lt:#FDF4E3;--green:#22863A;--green-lt:#E9F7EE; }

  .rc button { cursor: pointer; border: none; background: none; font: inherit; color: inherit; }
  .rc input  { font: inherit; }

  /* ── Page layout ── */
  .rc-page {
    display: flex; flex-direction: column; align-items: center;
    min-height: 100svh; padding: 0 0 60px;
  }

  /* ── Top header (dark band) ── */
  .rc-top {
    width: 100%; background: var(--ink);
    padding: 20px 24px 32px;
    display: flex; flex-direction: column; align-items: center; gap: 10px;
    position: relative; overflow: hidden;
  }
  .rc-top::after {
    content:''; position:absolute; bottom:-1px; left:0; right:0; height:24px;
    background: var(--bg); border-radius: 24px 24px 0 0;
  }
  .rc-salon-badge {
    display: flex; align-items: center; gap: 8px;
    padding: 6px 16px; border-radius: 100px;
    background: rgba(255,255,255,.10);
    font-size: 12px; font-weight: 700; color: rgba(255,255,255,.85);
    letter-spacing: .04em;
  }
  .rc-salon-dot { width:6px;height:6px;border-radius:50%;background:var(--accent);flex-shrink:0; }
  .rc-top-label {
    font-size: 11px; font-weight: 700; letter-spacing: .14em;
    text-transform: uppercase; color: rgba(255,255,255,.45);
  }
  .rc-top-name {
    font-family: 'Garamond','Georgia',serif;
    font-size: 32px; font-weight: normal; color: #fff;
    text-align: center; line-height: 1.15; text-wrap: balance;
    padding-bottom: 4px;
  }

  /* ── Body ── */
  .rc-body {
    width: 100%; max-width: 440px; padding: 20px 20px 0;
    display: flex; flex-direction: column; gap: 12px;
  }

  /* ── Date ticket ── */
  .rc-ticket {
    background: var(--surface); border-radius: var(--r);
    border: 1px solid var(--border);
    box-shadow: 0 2px 16px rgba(28,24,20,.06);
    overflow: hidden;
  }
  .rc-ticket-bar {
    height: 4px; background: linear-gradient(90deg, var(--accent), var(--gold));
  }
  .rc-ticket-body {
    display: flex; align-items: center; gap: 16px;
    padding: 18px 20px;
  }
  .rc-ticket-cal {
    width: 52px; height: 52px; flex-shrink: 0;
    background: var(--accent-lt); border-radius: 14px;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
  }
  .rc-ticket-cal-top {
    width: 100%; background: var(--accent); border-radius: 14px 14px 0 0;
    display: flex; align-items: center; justify-content: center;
    height: 14px;
  }
  .rc-ticket-cal-dot { width:4px;height:4px;border-radius:50%;background:rgba(255,255,255,.7);margin:0 2px; }
  .rc-ticket-cal-body {
    flex: 1; display: flex; align-items: center; justify-content: center;
    font-size: 22px; font-weight: 800; color: var(--accent);
  }
  .rc-ticket-info-label { font-size: 11px; font-weight: 700; letter-spacing:.08em; text-transform:uppercase; color:var(--ink3); margin-bottom:2px; }
  .rc-ticket-info-date  { font-size: 15px; font-weight: 700; color: var(--ink); line-height:1.3; }
  .rc-ticket-service {
    display: inline-flex; align-items: center; gap: 5px;
    margin-top: 6px; padding: 4px 10px;
    background: var(--gold-lt); color: var(--gold);
    border-radius: 100px; font-size: 11px; font-weight: 700;
  }

  /* ── Action cards ── */
  .rc-actions { display: flex; flex-direction: column; gap: 10px; }
  .rc-action-card {
    width: 100%; border-radius: var(--r);
    border: 2px solid var(--border);
    background: var(--surface);
    box-shadow: 0 2px 12px rgba(28,24,20,.05);
    padding: 18px 20px;
    display: flex; align-items: center; gap: 16px;
    text-align: left;
    transition: all .2s cubic-bezier(.34,1.3,.64,1);
    position: relative; overflow: hidden;
  }
  .rc-action-card:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(28,24,20,.12); }
  .rc-action-card:active { transform: translateY(0); }
  .rc-action-card.green:hover { border-color: var(--green); }
  .rc-action-card.teal:hover  { border-color: var(--accent); }
  .rc-action-icon-wrap {
    width: 52px; height: 52px; flex-shrink: 0;
    border-radius: 16px;
    display: flex; align-items: center; justify-content: center;
    font-size: 24px;
  }
  .rc-action-icon-wrap.green { background: var(--green-lt); }
  .rc-action-icon-wrap.teal  { background: var(--accent-lt); }
  .rc-action-title { font-size: 15px; font-weight: 700; color: var(--ink); margin-bottom: 3px; }
  .rc-action-sub   { font-size: 12px; color: var(--ink3); line-height: 1.4; }
  .rc-action-arrow {
    margin-left: auto; flex-shrink: 0;
    width: 32px; height: 32px; border-radius: 50%;
    background: var(--bg); border: 1px solid var(--border);
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; color: var(--ink3);
    transition: all .2s;
  }
  .rc-action-card:hover .rc-action-arrow { background: var(--ink); color: #fff; border-color: var(--ink); }

  .rc-decline {
    text-align: center; padding: 8px;
    font-size: 13px; color: var(--ink3);
    transition: color .15s;
  }
  .rc-decline:hover { color: #C0392B; }

  /* ── Step header ── */
  .rc-step-header {
    display: flex; align-items: center; gap: 12px;
    padding: 4px 0 4px;
  }
  .rc-back {
    width: 40px; height: 40px; flex-shrink: 0;
    border-radius: 50%;
    background: var(--surface); border: 1.5px solid var(--border);
    display: flex; align-items: center; justify-content: center;
    font-size: 20px; color: var(--ink2);
    box-shadow: 0 2px 8px rgba(28,24,20,.06);
    transition: all .15s;
  }
  .rc-back:hover { background: var(--ink); color: #fff; border-color: var(--ink); }
  .rc-step-title { font-size: 18px; font-weight: 700; color: var(--ink); }
  .rc-step-sub   { font-size: 12px; color: var(--ink3); margin-top: 2px; }

  /* ── Time slots ── */
  .rc-slots-card {
    background: var(--surface); border-radius: var(--r);
    padding: 20px; border: 1px solid var(--border);
    box-shadow: 0 2px 16px rgba(28,24,20,.06);
    display: flex; flex-direction: column; gap: 18px;
  }
  .rc-slots-group-label {
    font-size: 11px; font-weight: 800; letter-spacing: .12em;
    text-transform: uppercase; color: var(--ink3);
    display: flex; align-items: center; gap: 8px;
    margin-bottom: 10px;
  }
  .rc-slots-group-label::after { content:''; flex:1; height:1px; background:var(--border); }
  .rc-slots-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .rc-slot {
    display: flex; align-items: center; justify-content: center;
    padding: 14px 8px; border-radius: 12px;
    border: 2px solid var(--border);
    background: var(--surface);
    font-size: 15px; font-weight: 700; color: var(--ink);
    transition: all .18s ease;
  }
  .rc-slot:hover { border-color: var(--accent); background: var(--accent-lt); color: var(--accent); }
  .rc-slot.active {
    border-color: var(--accent); background: var(--accent);
    color: #fff;
    box-shadow: 0 4px 16px rgba(26,138,122,.3);
  }

  /* ── Summary box ── */
  .rc-summary {
    background: var(--accent-lt); border: 2px solid var(--accent);
    border-radius: var(--r-sm); padding: 16px 18px;
    display: flex; align-items: center; gap: 14px;
  }
  .rc-summary-emoji { font-size: 24px; flex-shrink: 0; }
  .rc-summary-main  { font-size: 16px; font-weight: 700; color: var(--accent-dk); }
  .rc-summary-meta  { font-size: 12px; color: var(--accent); margin-top: 2px; }

  /* ── Date input ── */
  .rc-date-input {
    width: 100%; padding: 14px 16px;
    border: 2px solid var(--border); border-radius: var(--r-sm);
    background: var(--surface); color: var(--ink);
    font-size: 15px; font-weight: 600;
    outline: none; transition: border-color .15s;
    appearance: none; -webkit-appearance: none;
  }
  .rc-date-input:focus { border-color: var(--accent); }

  /* ── CTA buttons ── */
  .rc-btn {
    width: 100%; padding: 17px;
    border-radius: var(--r-sm); font-size: 15px; font-weight: 700;
    transition: all .18s ease;
  }
  .rc-btn.primary {
    background: var(--accent); color: #fff;
    box-shadow: 0 4px 20px rgba(26,138,122,.28);
  }
  .rc-btn.primary:hover:not(:disabled) { background: var(--accent-dk); transform: translateY(-1px); box-shadow: 0 6px 28px rgba(26,138,122,.45); }
  .rc-btn.ghost  { color: var(--ink3); font-size: 13px; }
  .rc-btn.ghost:hover:not(:disabled) { color: var(--ink2); }
  .rc-btn:disabled { opacity: .35; cursor: not-allowed; }

  /* ── Done screen ── */
  .rc-done-card {
    background: var(--surface); border-radius: var(--r);
    border: 1px solid var(--border);
    box-shadow: 0 2px 16px rgba(28,24,20,.06);
    overflow: hidden; text-align: center;
  }
  .rc-done-top { height: 5px; }
  .rc-done-body { padding: 36px 24px 28px; }
  .rc-done-orb {
    width: 80px; height: 80px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 36px; margin: 0 auto 20px;
  }
  .rc-done-title {
    font-family: 'Garamond','Georgia',serif;
    font-size: 26px; font-weight: normal; color: var(--ink);
    margin-bottom: 8px; text-wrap: balance;
  }
  .rc-done-sub { font-size: 14px; color: var(--ink2); line-height: 1.6; }

  .rc-booking-list {
    background: var(--surface); border-radius: var(--r);
    border: 1px solid var(--border);
    box-shadow: 0 2px 16px rgba(28,24,20,.06);
    overflow: hidden;
  }
  .rc-booking-row {
    display: flex; align-items: center; gap: 14px;
    padding: 16px 20px; border-bottom: 1px solid var(--border);
  }
  .rc-booking-row:last-child { border-bottom: none; }
  .rc-booking-icon-wrap {
    width: 40px; height: 40px; flex-shrink: 0;
    border-radius: 12px; background: var(--accent-lt);
    display: flex; align-items: center; justify-content: center; font-size: 18px;
  }
  .rc-booking-lbl { font-size: 11px; font-weight: 700; letter-spacing: .07em; text-transform: uppercase; color: var(--ink3); }
  .rc-booking-val { font-size: 14px; font-weight: 700; color: var(--ink); margin-top: 1px; }

  .rc-call-btn {
    width: 100%; padding: 16px;
    background: var(--surface); border: 2px solid var(--border);
    border-radius: var(--r-sm);
    font-size: 14px; font-weight: 700; color: var(--ink2);
    display: flex; align-items: center; justify-content: center; gap: 8px;
    box-shadow: 0 2px 12px rgba(28,24,20,.06);
    transition: all .15s; text-decoration: none;
  }
  .rc-call-btn:hover { border-color: var(--accent); color: var(--accent); }

  /* ── Spinner / Error ── */
  .rc-center { min-height:100svh;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:24px;gap:16px;background:var(--bg); }
  .rc-spin { width:36px;height:36px;border:3px solid var(--border);border-top-color:var(--accent);border-radius:50%;animation:rc-spin .7s linear infinite; }
  @keyframes rc-spin { to { transform: rotate(360deg); } }
  .rc-spin-label { font-size:13px; color:var(--ink3); }
  .rc-err-card { background:var(--surface);border-radius:var(--r);padding:40px 28px;max-width:360px;width:100%;text-align:center;box-shadow:0 4px 24px rgba(28,24,20,.10);border:1px solid var(--border); }
  .rc-err-icon { font-size:40px; margin-bottom:16px; }
  .rc-err-title { font-size:20px;font-weight:700;color:var(--ink);margin-bottom:8px; }
  .rc-err-sub   { font-size:14px;color:var(--ink2);line-height:1.6; }

  /* ── Animations ── */
  @keyframes rc-in      { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:none} }
  @keyframes rc-in-left { from{opacity:0;transform:translateX(-18px)} to{opacity:1;transform:none} }
  @keyframes rc-in-right{ from{opacity:0;transform:translateX(18px)}  to{opacity:1;transform:none} }
  .rc-anim-in      { animation: rc-in       .22s ease both; }
  .rc-anim-right   { animation: rc-in-right .22s ease both; }
  .rc-anim-left    { animation: rc-in-left  .22s ease both; }

  @media (prefers-reduced-motion: reduce) {
    .rc-anim-in,.rc-anim-right,.rc-anim-left { animation: none; }
    .rc-spin { animation: none; }
  }
`;

/* ─── Composants internes ───────────────────────────────────────────── */
function CalIcon({ date }) {
  const d = date ? new Date(date) : null;
  const day = d ? d.getDate() : '–';
  return (
    <div className="rc-ticket-cal">
      <div className="rc-ticket-cal-top">
        <span className="rc-ticket-cal-dot" />
        <span className="rc-ticket-cal-dot" />
      </div>
      <div className="rc-ticket-cal-body">{day}</div>
    </div>
  );
}

function SlotGroup({ label, slots, selected, onSelect }) {
  return (
    <div>
      <div className="rc-slots-group-label">{label}</div>
      <div className="rc-slots-grid">
        {slots.map(s => (
          <button key={s}
            className={`rc-slot${selected === s ? ' active' : ''}`}
            onClick={() => onSelect(s === selected ? '' : s)}>
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <>
      <style>{STYLES}</style>
      <div className="rc">
        <div className="rc-center">
          <div className="rc-spin" />
          <span className="rc-spin-label">Chargement…</span>
        </div>
      </div>
    </>
  );
}

function ErrorScreen({ msg }) {
  return (
    <>
      <style>{STYLES}</style>
      <div className="rc">
        <div className="rc-center">
          <div className="rc-err-card">
            <div className="rc-err-icon">🔗</div>
            <div className="rc-err-title">Lien invalide</div>
            <div className="rc-err-sub">{msg}</div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Done screen ──────────────────────────────────────────────────── */
function DoneScreen({ result, info, chosenTime, chosenDate }) {
  const adminStatus = info?.adminResponseStatus;
  const clientDate  = chosenDate || info?.clientProposedDate;
  const dur         = info?.serviceDuration || 180;

  let emoji, title, sub, barColor, orbStyle, extra = null;

  if (result === 'confirmed') {
    emoji = '✅'; title = 'Parfait, c\'est noté !';
    sub = 'Votre passage est bien confirmé. Nous nous réjouissons de vous accueillir.';
    barColor = 'linear-gradient(90deg, #22863A, #1A8A7A)';
    orbStyle = { background: 'linear-gradient(135deg, #22863A, #1A8A7A)' };
  } else if (result === 'declined') {
    emoji = '🙏'; title = 'Dommage pour cette fois…';
    sub = 'Nous espérons vous revoir très bientôt. N\'hésitez pas à nous appeler.';
    barColor = 'linear-gradient(90deg, #8B6F4E, #C9922A)';
    orbStyle = { background: 'linear-gradient(135deg, #8B6F4E, #C9922A)' };
  } else if (!adminStatus) {
    emoji = '📬'; title = 'Demande envoyée !';
    sub = 'L\'équipe va examiner votre proposition et vous recontacter rapidement.';
    barColor = 'linear-gradient(90deg, #1A8A7A, #C9922A)';
    orbStyle = { background: 'linear-gradient(135deg, #1A8A7A, #C9922A)' };
  } else if (adminStatus === 'accepted') {
    emoji = '🎊'; title = 'Date confirmée !';
    sub = 'Le salon a accepté votre créneau. À très bientôt !';
    barColor = 'linear-gradient(90deg, #22863A, #1A8A7A)';
    orbStyle = { background: 'linear-gradient(135deg, #22863A, #1A8A7A)' };
  } else {
    emoji = '💬'; title = 'Nouvelle proposition';
    sub = 'Votre date n\'était pas disponible. Le salon vous propose une alternative :';
    barColor = 'linear-gradient(90deg, #1A8A7A, #C9922A)';
    orbStyle = { background: 'linear-gradient(135deg, #1A8A7A, #C9922A)' };
    extra = fmtDate(info?.adminProposedDate);
  }

  return (
    <>
      <style>{STYLES}</style>
      <div className="rc">
        <div className="rc-page">
          <div className="rc-top">
            <div className="rc-salon-badge">
              <span className="rc-salon-dot" />
              {info?.salonNom}
            </div>
          </div>

          <div className="rc-body rc-anim-in">
            <div className="rc-done-card">
              <div className="rc-done-top" style={{ background: barColor }} />
              <div className="rc-done-body">
                <div className="rc-done-orb" style={{ ...orbStyle, boxShadow: '0 8px 32px rgba(26,138,122,.3)' }}>
                  {emoji}
                </div>
                <div className="rc-done-title">{title}</div>
                <div className="rc-done-sub">{sub}</div>
                {extra && (
                  <div style={{
                    marginTop: 16, background: 'var(--gold-lt)',
                    border: '2px solid var(--gold)', borderRadius: 14,
                    padding: '12px 16px', color: 'var(--gold)',
                    fontWeight: 700, fontSize: 15
                  }}>
                    {extra}
                  </div>
                )}
              </div>
            </div>

            {result === 'confirmed' && (chosenTime || info?.nextVisitDate) && (
              <div className="rc-booking-list">
                {info?.nextVisitDate && (
                  <div className="rc-booking-row">
                    <div className="rc-booking-icon-wrap">📅</div>
                    <div>
                      <div className="rc-booking-lbl">Date</div>
                      <div className="rc-booking-val">{fmtDate(info.nextVisitDate)}</div>
                    </div>
                  </div>
                )}
                {chosenTime && (
                  <div className="rc-booking-row">
                    <div className="rc-booking-icon-wrap">🕐</div>
                    <div>
                      <div className="rc-booking-lbl">Créneau</div>
                      <div className="rc-booking-val">{chosenTime} · {fmtDuration(dur)}</div>
                    </div>
                  </div>
                )}
                {info?.serviceNom && (
                  <div className="rc-booking-row">
                    <div className="rc-booking-icon-wrap">✂️</div>
                    <div>
                      <div className="rc-booking-lbl">Service</div>
                      <div className="rc-booking-val">{info.serviceNom}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {result === 'rescheduled' && clientDate && (
              <div className="rc-booking-list">
                <div className="rc-booking-row">
                  <div className="rc-booking-icon-wrap">📅</div>
                  <div>
                    <div className="rc-booking-lbl">Date proposée</div>
                    <div className="rc-booking-val">
                      {fmtDate(clientDate)}{info?.clientPreferredTime ? ` à ${info.clientPreferredTime}` : ''}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {info?.salonTelephone && (
              <a href={`tel:${info.salonTelephone}`} className="rc-call-btn">
                📞 Appeler {info?.salonNom}
              </a>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Page principale ──────────────────────────────────────────────── */
export default function RappelConfirmPage() {
  const { token } = useParams();

  const [info,        setInfo]        = useState(null);
  const [error,       setError]       = useState('');
  const [pageLoading, setPageLoading] = useState(true);
  const [step,        setStep]        = useState('choice');
  const [direction,   setDirection]   = useState('forward');
  const [chosenTime,  setChosenTime]  = useState('');
  const [reschedDate, setReschedDate] = useState('');
  const [reschedTime, setReschedTime] = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [doneResult,  setDoneResult]  = useState(null);
  const prevStep = useRef('choice');

  useEffect(() => {
    axios.get(`${API}/rappels/public/${token}`)
      .then(r => {
        const d = r.data.data;
        setInfo(d);
        if (d.confirmationStatus !== 'pending') {
          setDoneResult(d.confirmationStatus);
          setStep('done');
        }
      })
      .catch(() => setError('Ce lien est invalide ou a expiré. Contactez directement le salon.'))
      .finally(() => setPageLoading(false));
  }, [token]);

  const go = (next) => {
    const order = ['choice', 'confirm_time', 'reschedule', 'done'];
    setDirection(order.indexOf(next) > order.indexOf(step) ? 'forward' : 'back');
    prevStep.current = step;
    setStep(next);
  };

  const submit = async (action, extra = {}) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/rappels/public/${token}`, { action, ...extra });
      if (action === 'confirmer') setDoneResult('confirmed');
      if (action === 'decliner')  setDoneResult('declined');
      if (action === 'reporter')  setDoneResult('rescheduled');
      setDirection('forward');
      setStep('done');
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer ou appeler le salon directement.');
    } finally {
      setSubmitting(false);
    }
  };

  if (pageLoading) return <Spinner />;
  if (error)       return <ErrorScreen msg={error} />;
  if (step === 'done') return (
    <DoneScreen result={doneResult} info={info}
      chosenTime={chosenTime}
      chosenDate={reschedDate ? new Date(reschedDate) : null} />
  );

  const dur   = info?.serviceDuration || 180;
  const today = new Date().toISOString().slice(0, 10);
  const anim  = direction === 'forward' ? 'rc-anim-right' : 'rc-anim-left';

  return (
    <>
      <style>{STYLES}</style>
      <div className="rc">
        <div className="rc-page">

          {/* ── Étape : choix ─────────────────────────────────── */}
          {step === 'choice' && (
            <>
              <div className="rc-top">
                <div className="rc-salon-badge">
                  <span className="rc-salon-dot" />
                  {info?.salonNom}
                </div>
                <div className="rc-top-label">Rappel de visite</div>
                <div className="rc-top-name">
                  Bonjour{info?.clientPrenom ? `, ${capitalize(info.clientPrenom)}` : ''}&nbsp;! 👋
                </div>
              </div>

              <div className={`rc-body ${anim}`}>
                {/* Date ticket */}
                {info?.nextVisitDate && (
                  <div className="rc-ticket">
                    <div className="rc-ticket-bar" />
                    <div className="rc-ticket-body">
                      <CalIcon date={info.nextVisitDate} />
                      <div style={{ flex: 1 }}>
                        <div className="rc-ticket-info-label">Prochain passage estimé</div>
                        <div className="rc-ticket-info-date">{fmtDate(info.nextVisitDate)}</div>
                        {info?.serviceNom && (
                          <div className="rc-ticket-service">
                            ✂️ {info.serviceNom} · {fmtDuration(dur)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="rc-actions">
                  <button className="rc-action-card green" onClick={() => go('confirm_time')}>
                    <div className="rc-action-icon-wrap green">✅</div>
                    <div style={{ flex: 1 }}>
                      <div className="rc-action-title">Je confirme ma venue</div>
                      <div className="rc-action-sub">Je suis disponible et je choisis mon heure</div>
                    </div>
                    <div className="rc-action-arrow">›</div>
                  </button>

                  <button className="rc-action-card teal" onClick={() => go('reschedule')}>
                    <div className="rc-action-icon-wrap teal">📅</div>
                    <div style={{ flex: 1 }}>
                      <div className="rc-action-title">Je préfère une autre date</div>
                      <div className="rc-action-sub">Proposer un créneau qui me convient mieux</div>
                    </div>
                    <div className="rc-action-arrow">›</div>
                  </button>
                </div>

                <button className="rc-decline" onClick={() => submit('decliner')} disabled={submitting}>
                  {submitting ? '…' : 'Je ne pourrai pas venir cette fois'}
                </button>
              </div>
            </>
          )}

          {/* ── Étape : choisir l'heure ───────────────────────── */}
          {step === 'confirm_time' && (
            <>
              <div className="rc-top" style={{ paddingBottom: 32 }}>
                <div className="rc-salon-badge">
                  <span className="rc-salon-dot" />
                  {info?.salonNom}
                </div>
              </div>

              <div className={`rc-body ${anim}`}>
                <div className="rc-step-header">
                  <button className="rc-back" onClick={() => go('choice')}>‹</button>
                  <div>
                    <div className="rc-step-title">Choisir mon heure</div>
                    {info?.nextVisitDate && (
                      <div className="rc-step-sub">{fmtDate(info.nextVisitDate)}</div>
                    )}
                  </div>
                </div>

                <div className="rc-slots-card">
                  <SlotGroup label="Matin"      slots={MORNING}   selected={chosenTime} onSelect={setChosenTime} />
                  <SlotGroup label="Après-midi" slots={AFTERNOON} selected={chosenTime} onSelect={setChosenTime} />
                </div>

                {chosenTime && (
                  <div className="rc-summary">
                    <span className="rc-summary-emoji">🕐</span>
                    <div>
                      <div className="rc-summary-main">{chosenTime} · {fmtDuration(dur)}</div>
                      <div className="rc-summary-meta">
                        {info?.nextVisitDate ? fmtDateShort(info.nextVisitDate) : ''}
                        {info?.serviceNom ? ` · ${info.serviceNom}` : ''}
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <button className="rc-btn primary"
                    disabled={!chosenTime || submitting}
                    onClick={() => submit('confirmer', { preferredTime: chosenTime, estimatedDuration: dur })}>
                    {submitting ? 'Confirmation…' : 'Confirmer ce créneau'}
                  </button>
                  <button className="rc-btn ghost" disabled={submitting}
                    onClick={() => submit('confirmer')}>
                    Confirmer sans préciser l'heure
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ── Étape : reporter ──────────────────────────────── */}
          {step === 'reschedule' && (
            <>
              <div className="rc-top" style={{ paddingBottom: 32 }}>
                <div className="rc-salon-badge">
                  <span className="rc-salon-dot" />
                  {info?.salonNom}
                </div>
              </div>

              <div className={`rc-body ${anim}`}>
                <div className="rc-step-header">
                  <button className="rc-back" onClick={() => go('choice')}>‹</button>
                  <div>
                    <div className="rc-step-title">Choisir une autre date</div>
                    <div className="rc-step-sub">Proposez un créneau qui vous convient</div>
                  </div>
                </div>

                <div className="rc-slots-card">
                  <div>
                    <div className="rc-slots-group-label">Date souhaitée</div>
                    <input type="date" className="rc-date-input"
                      value={reschedDate} min={today}
                      onChange={e => { setReschedDate(e.target.value); setReschedTime(''); }} />
                  </div>

                  {reschedDate && (
                    <>
                      <SlotGroup label="Matin · optionnel"      slots={MORNING}   selected={reschedTime} onSelect={t => setReschedTime(t === reschedTime ? '' : t)} />
                      <SlotGroup label="Après-midi · optionnel" slots={AFTERNOON} selected={reschedTime} onSelect={t => setReschedTime(t === reschedTime ? '' : t)} />
                    </>
                  )}
                </div>

                {reschedDate && (
                  <div className="rc-summary">
                    <span className="rc-summary-emoji">📅</span>
                    <div>
                      <div className="rc-summary-main">
                        {fmtDate(reschedDate)}{reschedTime ? ` à ${reschedTime}` : ''}
                      </div>
                      <div className="rc-summary-meta">Le salon confirmera votre nouveau créneau</div>
                    </div>
                  </div>
                )}

                <button className="rc-btn primary"
                  disabled={!reschedDate || submitting}
                  onClick={() => submit('reporter', {
                    proposedDate:      reschedDate,
                    preferredTime:     reschedTime || undefined,
                    estimatedDuration: dur,
                  })}>
                  {submitting ? 'Envoi…' : 'Envoyer ma demande'}
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
}
